import API_CONFIG from '../../config/api.config';
import AuthService from './auth.service';
import { fetchWithRetry, fetchWithAuthRetry } from './api.utils';

interface User {
  username: string;
  userToken: string;
}

interface Activity {
  id: number;
  activityType: string;
  activity: string;
  modifiedItem: any;
  modifiedFields: Record<string, any>;
  observations: string;
  createdDate: string;
  user: User;
}

interface ActivityGroup {
  count: number;
  activities: Activity[];
}

interface ProductHistoryResponse {
  status: string;
  sku: string;
  productId: number;
  execution_time_seconds: number;
  groups: {
    sales_and_returns: ActivityGroup;
    all_activities: ActivityGroup;
  };
}

interface SkuSearchParams {
  temporaryPlace?: string;
  search?: string;
  sku?: string;
  skus?: string[];
  vehicle_sku?: string;
  quantity_gt?: number;
  quantity_eq?: number;
  manufacturer_category?: string | number;
  manufacturer?: string | number;
  model?: string | number;
  models?: (string | number)[];
  component?: string | number;
  components?: (string | number)[];
  filters?: (string | number)[] | (string | number)[][];
  component_category?: string | number;
  oemCode?: string;
  oemCodes?: string[];
  searchOemCodes?: string[];
  idItem?: string | number;
  engine?: string | number;
  engineCode?: string | number;
  idWarehouseArea?: string | number;
  idWarehouseAreas?: (string | number)[];
  createdByUserId?: string | number;
  assignNextResearchStatusId?: string | number;
  filterResearchStatusId?: string | number;
  quick_search?: boolean;
  skip?: number;
  limit?: number;
  productCreatedDate?: string;
  productCreatedDateFrom?: string;
  productCreatedDateTo?: string;
  price_min?: number;
  price_max?: number;
  compatibilityTargets?: (string | number)[];
  redact_locations?: boolean;
}

const buildSkuSearchPayload = (
  params: SkuSearchParams,
  extra: Record<string, unknown> = {}
) => {
  const payload: Record<string, unknown> = {};
  const add = (key: string, value: unknown) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value) && value.length === 0) return;
    payload[key] = value;
  };

  add('temporaryPlace', params.temporaryPlace);
  add('search', params.search);
  add('sku', params.sku);
  add('skus', params.skus);
  add('vehicle_sku', params.vehicle_sku);
  add('quantity_gt', params.quantity_gt);
  add('quantity_eq', params.quantity_eq);
  add('manufacturer_category', params.manufacturer_category);
  add('manufacturer', params.manufacturer);
  add('model', params.model);
  add('models', params.models);
  add('component', params.component);
  add('components', params.components);
  add('filters', params.filters);
  add('component_category', params.component_category);
  add('oemCode', params.oemCode);
  add('oemCodes', params.oemCodes);
  add('searchOemCodes', params.searchOemCodes);
  add('idItem', params.idItem);
  add('engine', params.engine);
  add('engineCode', params.engineCode);
  add('idWarehouseArea', params.idWarehouseArea);
  add('idWarehouseAreas', params.idWarehouseAreas);
  add('createdByUserId', params.createdByUserId);
  add('assignNextResearchStatusId', params.assignNextResearchStatusId);
  add('filterResearchStatusId', params.filterResearchStatusId);
  add('quick_search', params.quick_search);
  add('skip', params.skip);
  add('limit', params.limit);
  add('productCreatedDate', params.productCreatedDate);
  add('productCreatedDateFrom', params.productCreatedDateFrom);
  add('productCreatedDateTo', params.productCreatedDateTo);
  add('price_min', params.price_min);
  add('price_max', params.price_max);
  add('compatibilityTargets', params.compatibilityTargets);
  add('redact_locations', params.redact_locations);

  Object.keys(extra).forEach(key => add(key, extra[key]));
  return payload;
};

interface PartnerListingData {
  id: number;
  itemId: string;
  imageCollectionId: number;
  link: string;
  price: number;
  currency: string;
  class: string;
  createdDate: string;
}

interface Partner {
  id: number;
  name: string;
  token: string;
  productPathLink: string;
  isListed: boolean;
  listingData: PartnerListingData | null;
}

interface SalePartnersResponse {
  status: string;
  sku: string;
  productId: number;
  stockId: number;
  execution_time_seconds: number;
  partners: Partner[];
}

interface ManageListingResponse {
  status: string;
  message: string;
  sku: string;
  partner_id: number;
  action: 'list' | 'unlist';
  listing_data: PartnerListingData | null;
  execution_time_seconds: number;
}

interface UpdateSkuPayload {
  sku: string;
  vehicleSku?: string | null;
  manufacturer_category?: string | number;
  manufacturer?: string | number;
  model?: string | number;
  engine?: string | number;
  engineCode?: string | number;
  component?: string | number;
  oemCode?: string[];
  sellingPrice?: number;
  pieceManufacturer?: string;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  details?: string;
  observations?: string;
  researchStatusId?: number;
  nextStatusId?: number;
  resetToDraft?: boolean;
}

interface UpdateSkuResponse {
  status: string;
  message: string;
  sku: string;
  execution_time_seconds: number;
}

// Add interface for vehicle SKU response
interface VehicleSkuResponse {
  status: string;
  skus: Array<{
    sku: string;
    images: Array<{image: string; cover?: number}>;
    manufacturer: {id: number; value: string};
    model: {id: number; value: string};
    engine?: {id: number; value: string};
    engineCode?: {id: number; value: string};
    vinCode?: string;
    manufacturingYear?: number;
    steeringWheelSide?: string;
    vehicleDetails?: string;
    quantity: number;
    // Other common fields
    createdDate?: string;
    // Location fields
    warehouseAreas?: Array<{id: number; name: string; temporaryPlace?: string}>;
  }>;
  skip: number;
  limit: number;
  execution_time_seconds: number;
}

interface VehicleSkuSearchParams {
  temporaryPlace?: string;
  search?: string;
  vehicle_sku?: string;
  quantity_gt?: number;
  quantity_eq?: number;
  manufacturer_category?: string | number;
  manufacturer?: string | number;
  model?: string | number;
  models?: (string | number)[];
  engine?: string | number;
  engineCode?: string | number;
  idWarehouseArea?: string | number;
  idWarehouseAreas?: (string | number)[];
  createdByUserId?: string | number;
  skip?: number;
  limit?: number;
}

interface ProductLocation {
  idMPSL: number;
  idProductStock: number;
  idWarehouseArea: number;
  warehouseArea: string;
  quantity: number;
  temporaryPlace: string | null;
  createdDate: string;
  updateDate: string | null;
}

interface ProductLocationsResponse {
  status: string;
  sku: string;
  productId: number;
  execution_time_seconds: number;
  total_quantity: number;
  locations_count: number;
  locations: ProductLocation[];
}

// Add the interface definitions after the ProductLocationsResponse interface
interface TransferPartRequest {
  sku: string;
  source_location: string;
  destination_location: string;
  quantity: number;
  source_warehouse_area_id?: number;
  destination_warehouse_area_id?: number;
  comments?: string;
}

interface TransferPartResponse {
  status: string;
  message: string;
  product_id: number;
  source_location: {
    location: string;
    remaining_quantity: number;
  };
  destination_location: {
    location: string;
    new_quantity: number;
  };
  transferred_quantity: number;
  execution_time_seconds?: number;
}

// Add image management interfaces
interface ImageEdit {
  rotation?: number;
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  annotations?: Array<{
    type: 'arrow' | 'text';
    color: string;
    points?: number[]; // For arrows
    x?: number; // For text
    y?: number; // For text
    text?: string; // For text
  }>;
}

interface AddedImage {
  tempId: string;
  imageBlob?: Blob;   // preferred: goes straight into FormData
  imageData?: string; // legacy Base64 data URL (converted to Blob before send)
  metadata: {
    cover?: boolean;
    public?: boolean;
    edits?: ImageEdit;
  };
}

interface ModifiedImage {
  image: string;
  updates: {
    cover?: boolean;
    public?: boolean;
    position?: number;
    edits?: ImageEdit;
  };
}

interface ImageBatchUpdatePayload {
  sku: string;
  changes: {
    added: AddedImage[];
    modified: ModifiedImage[];
    deleted: string[];
    coverImage?: string;
  };
}

interface ImageBatchUpdateResponse {
  status: string;
  message: string;
  sku: string;
  updated_images: Array<{
    image: string;
    cover?: number;
    public?: number;
  }>;
  execution_time_seconds: number;
}

// Add interfaces for add part/vehicle
interface ComponentData {
  component_id: number;
  selected_filters: Array<{
    id: number;
    value: boolean;
  }>;
}

interface VehicleData {
  manufacturer: { id: number; value: string };
  category: { id: number; value: string };
  model: { id: number; value: string };
  engine?: {
    id: number;
    value: string;
    engine_codes?: Array<{
      id: number;
      value: string;
      years?: string;
    }>;
  };
}

interface AddPartRequest {
  locationSku: string;
  partSku: string;
  quantity: number;
  warehouse_area_id?: number;
  partDetails?: string;
  observations?: string;
  selling_price?: number;
  component_data?: ComponentData;
  vehicleSku?: string;
  vehicle_data?: VehicleData;
  fullCar?: boolean;
  vinCode?: string;
  manufacturingYear?: number;
  steeringWheelSide?: number; // 1 = LHD, 2 = RHD
  acquisitionDate?: string; // YYYY-MM-DD
  oem_numbers?: string | string[];
  bolt_pattern?: number | null;
  image_orientations?: number[];
  images: File[];
}

interface AddPartResponse {
  status: string;
  images: Array<{
    filename: string;
    large_url: string;
    small_url: string;
  }>;
}

interface InvoicingPartName {
  component_id: number;
  invoicing_part_name: string;
}

class SkuService {
  private static instance: SkuService | null = null;

  static getInstance(): SkuService {
    if (!SkuService.instance) {
      SkuService.instance = new SkuService();
    }
    return SkuService.instance;
  }

  /**
   * Small delay to ensure preflight request completes
   * @param ms Time in milliseconds
   */
  private async preflightDelay(ms = 100) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async getSkus({
    temporaryPlace,
    search,
    sku,
    skus,
    vehicle_sku,
    quantity_gt,
    quantity_eq,
    manufacturer_category,
    manufacturer,
    model,
    models,
    component,
    components,
    filters,
    component_category,
    oemCode,
    oemCodes,
    searchOemCodes,
    idItem,
    engine,
    engineCode,
    idWarehouseArea,
    idWarehouseAreas,
    createdByUserId,
    assignNextResearchStatusId,
    filterResearchStatusId,
    quick_search,
    skip = 0,
    limit = 50,
    productCreatedDate,
    productCreatedDateFrom,
    productCreatedDateTo,
    price_min,
    price_max,
    compatibilityTargets,
    redact_locations
  }: SkuSearchParams = {}) {
    try {
      const usePost = [oemCodes, searchOemCodes].some(codes => Array.isArray(codes) && codes.length > 0);
      if (usePost) {
        const payload = buildSkuSearchPayload({
          temporaryPlace,
          search,
          sku,
          skus,
          vehicle_sku,
          quantity_gt,
          quantity_eq,
          manufacturer_category,
          manufacturer,
          model,
          models,
          component,
          components,
          filters,
          component_category,
          oemCode,
          oemCodes,
          searchOemCodes,
          idItem,
          engine,
          engineCode,
          idWarehouseArea,
          idWarehouseAreas,
          createdByUserId,
          assignNextResearchStatusId,
          filterResearchStatusId,
          quick_search,
          skip,
          limit,
          productCreatedDate,
          productCreatedDateFrom,
          productCreatedDateTo,
          price_min,
          price_max,
          compatibilityTargets,
          redact_locations
        });

        await this.preflightDelay();

        const response = await fetchWithRetry(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SEARCH_SKUS}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AuthService.getAccessToken()}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'max-age=3600',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          ...(redact_locations ? { cache: 'no-store' } : {}),
          credentials: 'include',
          mode: 'cors',
          body: JSON.stringify(payload)
        }, 3, 60000);

        if (!response.ok) {
          throw new Error('Failed to fetch SKUs');
        }

        const data = await response.json();

        if (data.status !== 'success') {
          throw new Error('Invalid response format');
        }

        return {
          skus: data.skus.map((sku: any) => ({
            ...sku,
            vehicleString: sku.vehicleString || '',
            engineString: sku.engineString || '',
            vehicleDetails: sku.vehicleDetails || ''
          })),
          skip: data.skip,
          limit: data.limit,
          execution_time_seconds: data.execution_time_seconds
        };
      }

      // Build base URL
      let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_SKUS}?`;
      
      // Build query parameters
      const params = new URLSearchParams();
      if (temporaryPlace) params.append('temporaryPlace', temporaryPlace);
      if (search) params.append('search', search);
      if (sku) params.append('sku', sku);
      if (skus) {
        skus.forEach(s => params.append('skus', s));
      }
      if (vehicle_sku) params.append('vehicle_sku', vehicle_sku);
      if (oemCode) params.append('oemCode', oemCode);
      if (idItem) params.append('idItem', idItem.toString());
      if (quantity_gt !== undefined && quantity_gt !== null) params.append('quantity_gt', quantity_gt.toString());
      if (quantity_eq !== undefined && quantity_eq !== null) params.append('quantity_eq', quantity_eq.toString());
      if (manufacturer_category) params.append('manufacturer_category', manufacturer_category.toString());
      if (manufacturer) params.append('manufacturer', manufacturer.toString());
      if (model) params.append('model', model.toString());
      if (models && models.length > 0) {
        params.append('models', models.join(','));
      }
      if (component) params.append('component', component.toString());
      if (components && components.length > 0) {
        params.append('components', components.join(','));
      }
      if (component_category) params.append('component_category', component_category.toString());
      if (compatibilityTargets && compatibilityTargets.length > 0) {
        params.append('compatibilityTargets', compatibilityTargets.join(','));
      }
      // Handle component_filters - can be a flat array or an array of arrays
      if (filters) {
        if (Array.isArray(filters)) {
          if (filters.length > 0) {
            // Check if we have a 2D array (grouped filters) or a flat array
            if (Array.isArray(filters[0])) {
              // It's a 2D array, each inner array is a group of filters
              (filters as (string | number)[][]).forEach(filterGroup => {
                if (filterGroup.length > 0) {
                  params.append('filters', `[${filterGroup.join(',')}]`);
                }
              });
            } else {
              // It's a flat array, treat it as a single group
              params.append('filters', `[${(filters as (string | number)[]).join(',')}]`);
            }
          }
        }
      }
      if (engine) params.append('engine', engine.toString());
      if (engineCode) params.append('engineCode', engineCode.toString());
      if (idWarehouseArea) params.append('idWarehouseArea', idWarehouseArea.toString());
      if (createdByUserId) params.append('createdByUserId', createdByUserId.toString());
      if (assignNextResearchStatusId) params.append('assignNextResearchStatusId', assignNextResearchStatusId.toString());
      if (filterResearchStatusId) params.append('filterResearchStatusId', filterResearchStatusId.toString());
      if (productCreatedDate) params.append('productCreatedDate', productCreatedDate);
      if (productCreatedDateFrom) params.append('productCreatedDateFrom', productCreatedDateFrom);
      if (productCreatedDateTo) params.append('productCreatedDateTo', productCreatedDateTo);
      if (price_min !== undefined && price_min !== null) params.append('price_min', price_min.toString());
      if (price_max !== undefined && price_max !== null) params.append('price_max', price_max.toString());
      if (redact_locations !== undefined) params.append('redact_locations', String(redact_locations));
      if (skip !== undefined) params.append('skip', skip.toString());
      if (limit !== undefined) params.append('limit', limit.toString());
      
      // Add cache-busting timestamp
      params.append('_ts', Date.now().toString());

      // Convert params to string
      const queryString = params.toString();
      
      // Combine URL parts
      url += queryString;
      
      // Add idWarehouseAreas separately to avoid URL encoding
      if (idWarehouseAreas) {
        url += `${queryString ? '&' : ''}idWarehouseAreas=${idWarehouseAreas.join(',')}`;
      }

      // Add small delay to ensure preflight completes before making request
      await this.preflightDelay();

      const response = await fetchWithAuthRetry(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'max-age=3600',
          'Accept': 'application/json',
          // Add CORS specific headers to help with preflight caching
          'X-Requested-With': 'XMLHttpRequest'
        },
        ...(redact_locations ? { cache: 'no-store' } : {}),
        credentials: 'include',
        mode: 'cors'
      }, 3, 60000, () => AuthService.refreshAccessToken()); // Increase timeout for potentially slow responses

      if (!response.ok) {
        throw new Error('Failed to fetch SKUs');
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error('Invalid response format');
      }

      return {
        skus: data.skus.map((sku: any) => ({
          ...sku,
          vehicleString: sku.vehicleString || '',
          engineString: sku.engineString || '',
          vehicleDetails: sku.vehicleDetails || ''
        })),
        skip: data.skip,
        limit: data.limit,
        execution_time_seconds: data.execution_time_seconds
      };
    } catch (error) {
      console.error('Error fetching SKUs:', error);
      throw error;
    }
  }

  async getSkusCount(params: SkuSearchParams = {}) {
    try {
      const usePost = [params.oemCodes, params.searchOemCodes].some(codes => Array.isArray(codes) && codes.length > 0);
      if (usePost) {
        const payload = buildSkuSearchPayload(params, { count_only: true });

        await this.preflightDelay();

        const response = await fetchWithRetry(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SEARCH_SKUS}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AuthService.getAccessToken()}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'max-age=3600',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include',
          mode: 'cors',
          body: JSON.stringify(payload)
        }, 3, 60000);

        if (!response.ok) {
          throw new Error('Failed to fetch SKUs');
        }

        const data = await response.json();

        if (data.status !== 'success') {
          throw new Error('Invalid response format');
        }

        return data;
      }

      // Build base URL
      let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_SKUS}?`;
      
      // Build query parameters
      const searchParams = new URLSearchParams();
      searchParams.append('count_only', 'true');  // Add count_only parameter
      
      // Handle component_filters separately
      const { filters, idWarehouseAreas, filterResearchStatusId, component_category, ...otherParams } = params;
      
      // Process regular parameters
      Object.keys(otherParams).forEach(key => {
        const value = (otherParams as Record<string, unknown>)[key];
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            // This logic will now be handled by joining the array into a comma-separated string
            if (key === 'models' || key === 'components' || key === 'compatibilityTargets') {
              if (value.length > 0) {
                searchParams.append(key, value.join(','));
              }
            } else {
              value.forEach(v => searchParams.append(key, v.toString()));
            }
          } else {
            searchParams.append(key, value.toString());
          }
        }
      });
      
      // Handle component_category
      if (component_category) {
        searchParams.append('component_category', component_category.toString());
      }
      
      // Add filterResearchStatusId if provided
      if (filterResearchStatusId) {
        searchParams.append('filterResearchStatusId', filterResearchStatusId.toString());
      }

      // Handle component_filters - can be a flat array or an array of arrays
      if (filters) {
        if (Array.isArray(filters)) {
          if (filters.length > 0) {
            // Check if we have a 2D array (grouped filters) or a flat array
            if (Array.isArray(filters[0])) {
              // It's a 2D array, each inner array is a group of filters
              (filters as (string | number)[][]).forEach(filterGroup => {
                if (filterGroup.length > 0) {
                  searchParams.append('filters', `[${filterGroup.join(',')}]`);
                }
              });
            } else {
              // It's a flat array, treat it as a single group
              searchParams.append('filters', `[${(filters as (string | number)[]).join(',')}]`);
            }
          }
        }
      }

      // Convert params to string
      const queryString = searchParams.toString();
      
      // Combine URL parts
      url += queryString;
      
      // Add idWarehouseAreas separately to avoid URL encoding
      if (idWarehouseAreas) {
        url += `${queryString ? '&' : ''}idWarehouseAreas=${idWarehouseAreas.join(',')}`;
      }

      const response = await fetchWithAuthRetry(
        url,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${AuthService.getAccessToken()}`,
            'Content-Type': 'application/json',
          },
        },
        1,
        30000,
        () => AuthService.refreshAccessToken(),
      );

      if (!response.ok) {
        throw new Error('Failed to fetch SKUs count');
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Error fetching SKUs count:', error);
      throw error;
    }
  }

  async getProductHistory(sku: string): Promise<ProductHistoryResponse> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_PRODUCT_ACTIVITIES}?sku=${sku}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching product history:', error);
      throw error;
    }
  }

  async getSalePartners(sku: string): Promise<SalePartnersResponse> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_SALE_PARTNERS}?sku=${sku}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sale partners');
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Error fetching sale partners:', error);
      throw error;
    }
  }

  async manageSalePartnerListing(
    sku: string,
    partnerId: number,
    action: 'list' | 'unlist',
    options?: { itemId?: string | number; priceEur?: number }
  ): Promise<ManageListingResponse> {
    try {
      // Build URL with optional params for special partners (e.g., eBay FR)
      const params = new URLSearchParams({ sku, partner_id: String(partnerId), action });
      if (options?.itemId !== undefined && options?.itemId !== null) {
        params.append('item_id', String(options.itemId));
      }
      if (options?.priceEur !== undefined && options?.priceEur !== null) {
        params.append('price_eur', String(options.priceEur));
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MANAGE_SALE_PARTNER_LISTING}?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AuthService.getAccessToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // Throw the error with the full detail structure
        throw data;
      }
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Error managing sale partner listing:', error);
      throw error;
    }
  }

  async getVehicleSkus(params: VehicleSkuSearchParams = {}) {
    try {
      // Build base URL
      let url = `${API_CONFIG.BASE_URL}/stoc/async/vehicle-skus-data?_ts=${Date.now()}`;
      
      // Build query parameters
      const searchParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        const value = (params as Record<string, unknown>)[key];
        if (value !== undefined && value !== null && key !== 'idWarehouseAreas') {
          if (Array.isArray(value)) {
            if (key === 'models') {
              if (value.length > 0) {
                searchParams.append(key, value.join(','));
              }
            } else {
              value.forEach(v => searchParams.append(key, v.toString()));
            }
          } else {
            searchParams.append(key, value.toString());
          }
        }
      });
      
      // Convert params to string
      const queryString = searchParams.toString();
      
      // Combine URL parts
      if (queryString) {
        url += `&${queryString}`;
      }
      
      // Add idWarehouseAreas separately to avoid URL encoding issues
      if (params.idWarehouseAreas) {
        url += `${queryString ? '&' : ''}idWarehouseAreas=${params.idWarehouseAreas.join(',')}`;
      }

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch vehicle SKUs');
      }
      
      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error('Invalid response format when fetching vehicle SKUs');
      }
      
      return data as VehicleSkuResponse;
    } catch (error) {
      console.error('Error fetching vehicle SKUs:', error);
      throw error;
    }
  }

  async updateSku(payload: UpdateSkuPayload): Promise<UpdateSkuResponse> {
    try {
      const url = `${API_CONFIG.BASE_URL}/stoc/async/update-sku`;
      
      const response = await fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        credentials: 'include',
        mode: 'cors'
      });

      if (!response.ok) {
        // The backend rejects incomplete research with a structured 400
        // (`detail.message` + `detail.problems`). Surfacing the generic text
        // here would hide the one thing the user needs: which field is wrong.
        let message = 'Failed to update SKU';
        try {
          const body = await response.json();
          const detail = body?.detail;
          if (typeof detail === 'string') {
            message = detail;
          } else if (detail?.message) {
            message = detail.message;
          } else if (body?.message) {
            message = body.message;
          }
        } catch {
          // Non-JSON error body — keep the generic message.
        }
        throw new Error(message);
      }

      const data = await response.json();

      if (data.status !== 'success') {
        throw new Error(data.message || 'Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Error updating SKU:', error);
      throw error;
    }
  }

  async getVehicleSkusCount(params: VehicleSkuSearchParams = {}) {
    try {
      // Build base URL
      let url = `${API_CONFIG.BASE_URL}/stoc/async/vehicle-skus-data?`;
      
      // Build query parameters
      const searchParams = new URLSearchParams();
      searchParams.append('count_only', 'true');  // Add count_only parameter
      
      Object.keys(params).forEach(key => {
        const value = (params as Record<string, unknown>)[key];
        if (value !== undefined && value !== null && key !== 'idWarehouseAreas') {
          if (Array.isArray(value)) {
            if (key === 'models') {
              if (value.length > 0) {
                searchParams.append(key, value.join(','));
              }
            } else {
              value.forEach(v => searchParams.append(key, v.toString()));
            }
          } else {
            searchParams.append(key, value.toString());
          }
        }
      });
      
      // Add timestamp to prevent caching
      searchParams.append('_ts', Date.now().toString());

      // Convert params to string
      const queryString = searchParams.toString();
      
      // Combine URL parts
      url += queryString;
      
      // Add idWarehouseAreas separately to avoid URL encoding issues
      if (params.idWarehouseAreas) {
        url += `${queryString ? '&' : ''}idWarehouseAreas=${params.idWarehouseAreas.join(',')}`;
      }

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch vehicle SKUs count');
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Error fetching vehicle SKUs count:', error);
      throw error;
    }
  }

  async getProductLocations(
    sku: string,
    options: { signal?: AbortSignal } = {}
  ): Promise<ProductLocationsResponse> {
    try {
      const params = new URLSearchParams({ sku });
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_PRODUCT_LOCATIONS}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store',
        signal: options.signal
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const message = typeof body?.detail === 'string'
          ? body.detail
          : body?.detail?.message || 'Nu s-au putut încărca locațiile.';
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw error;
      console.error('Error fetching product locations:', error);
      throw error;
    }
  }

  async addProductActivity(payload: {
    sku: string;
    activity_type: number;
    activity: number;
    modified_item?: number | null;
    modified_fields?: Record<string, any> | null;
    observations?: string | null;
  }) {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/stoc/async/add_product_activity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw data;
      }

      return data;
    } catch (error) {
      console.error('Error adding product activity:', error);
      throw error;
    }
  }

  async getAIResearch(sku: string) {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_AI_RESEARCH}?sku=${sku}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch AI research data');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching AI research data:', error);
      throw error;
    }
  }

  // Add the transferPart method after the getAIResearch method
  async transferPart(transferData: TransferPartRequest): Promise<TransferPartResponse> {
    try {
      const url = `${API_CONFIG.BASE_URL}/stoc/async/transfer_part`;
      
      const response = await fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferData),
        credentials: 'include',
        mode: 'cors'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to transfer part');
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Error transferring part:', error);
      throw error;
    }
  }

  async batchUpdateImages(payload: ImageBatchUpdatePayload): Promise<ImageBatchUpdateResponse> {
    try {
      const url = `${API_CONFIG.BASE_URL}/stoc/async/batch-update-images`;
      
      // Convert to FormData if we have new images
      let body: FormData | string;
      let headers: Record<string, string> = {
        'Authorization': `Bearer ${AuthService.getAccessToken()}`
      };

      if (payload.changes.added.length > 0) {
        const formData = new FormData();
        
        // Add the changes metadata
        formData.append('changes', JSON.stringify({
          modified: payload.changes.modified,
          deleted: payload.changes.deleted,
          coverImage: payload.changes.coverImage
        }));
        
        // Add each new image file. Blobs go straight into FormData; the
        // base64 branch only remains for legacy callers still sending
        // imageData data URLs.
        payload.changes.added.forEach((addedImage, index) => {
          let blob = addedImage.imageBlob;
          if (!blob && addedImage.imageData) {
            const base64Data = addedImage.imageData.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            blob = new Blob([new Uint8Array(byteNumbers)], { type: 'image/jpeg' });
          }
          if (!blob) {
            throw new Error(`Added image ${addedImage.tempId} has no imageBlob/imageData`);
          }

          formData.append(`image_${index}`, blob, `${addedImage.tempId}.jpg`);
          formData.append(`metadata_${index}`, JSON.stringify(addedImage.metadata));
        });
        
        formData.append('sku', payload.sku);
        body = formData;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(payload);
      }

      const response = await fetchWithRetry(url, {
        method: 'POST',
        headers,
        body,
        credentials: 'include',
        mode: 'cors'
      }, 3, 120000); // 2 minute timeout for image uploads

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update images');
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(data.message || 'Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Error updating images:', error);
      throw error;
    }
  }

  /**
   * Upload one or more videos for an in-stock part/vehicle. Videos are enqueued
   * for background transcoding (separate from images) and returned as
   * `status:'transcoding'` placeholders; the WS `transcode_update` push flips
   * them to `ready` later. Returns the record's full `videos` list.
   */
  async uploadSkuVideos(sku: string, files: File[]): Promise<{ status: string; videos: any[] }> {
    const url = `${API_CONFIG.BASE_URL}/stoc/async/skus/${encodeURIComponent(sku)}/videos`;
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));

    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${AuthService.getAccessToken()}` },
      body: formData,
      credentials: 'include',
      mode: 'cors',
    }, 2, 180000); // 3 min: large raw video bodies

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.detail?.message || errorData.message || 'Failed to upload videos');
    }
    return response.json();
  }

  /** Remove a single video entry (by entry_id) from an in-stock part/vehicle. */
  async deleteSkuVideo(sku: string, entryId: string): Promise<{ status: string; videos: any[] }> {
    const url = `${API_CONFIG.BASE_URL}/stoc/async/skus/${encodeURIComponent(sku)}/videos/${encodeURIComponent(entryId)}`;
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${AuthService.getAccessToken()}` },
      credentials: 'include',
      mode: 'cors',
    }, 2, 30000);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.detail?.message || errorData.message || 'Failed to delete video');
    }
    return response.json();
  }

  async addPart(data: AddPartRequest): Promise<AddPartResponse> {
    try {
      const url = `${API_CONFIG.BASE_URL}/stoc/async/add_part`;
      
      // Create FormData
      const formData = new FormData();
      
      // Add all fields to formData
      formData.append('locationSku', data.locationSku);
      formData.append('partSku', data.partSku);
      formData.append('quantity', data.quantity.toString());
      
      if (data.warehouse_area_id) {
        formData.append('warehouse_area_id', data.warehouse_area_id.toString());
      }
      
      if (data.partDetails) {
        formData.append('partDetails', data.partDetails);
      }
      
      if (data.observations) {
        formData.append('observations', data.observations);
      }
      
      if (data.selling_price !== undefined) {
        formData.append('selling_price', data.selling_price.toString());
      }
      
      if (data.component_data) {
        formData.append('component_data', JSON.stringify(data.component_data));
      }
      
      if (data.vehicleSku) {
        formData.append('vehicleSku', data.vehicleSku);
      }
      
      if (data.vehicle_data) {
        formData.append('vehicle_data', JSON.stringify(data.vehicle_data));
      }
      
      if (data.fullCar !== undefined) {
        formData.append('fullCar', data.fullCar.toString());
      }
      
      if (data.vinCode) {
        formData.append('vinCode', data.vinCode);
      }
      
      if (data.manufacturingYear !== undefined) {
        formData.append('manufacturingYear', data.manufacturingYear.toString());
      }
      
      if (data.steeringWheelSide !== undefined) {
        formData.append('steeringWheelSide', data.steeringWheelSide.toString());
      }
      
      if (data.acquisitionDate) {
        formData.append('acquisitionDate', data.acquisitionDate);
      }

      if (data.bolt_pattern !== undefined && data.bolt_pattern !== null) {
        formData.append('bolt_pattern', data.bolt_pattern.toString());
      }
      
      if (data.oem_numbers) {
        if (Array.isArray(data.oem_numbers)) {
          formData.append('oem_numbers', JSON.stringify(data.oem_numbers));
        } else {
          formData.append('oem_numbers', data.oem_numbers);
        }
      }
      
      if (data.image_orientations) {
        formData.append('image_orientations', JSON.stringify(data.image_orientations));
      }
      
      // Add images
      data.images.forEach((image) => {
        formData.append('images', image);
      });
      
      const response = await fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`
          // Note: Don't set Content-Type header, let browser set it with boundary
        },
        body: formData,
        credentials: 'include',
        mode: 'cors'
      }, 3, 120000); // 2 minute timeout for image uploads

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific error codes
        if (response.status === 422 && errorData.detail) {
          const error = errorData.detail.error;
          const message = errorData.detail.message;
          
          // Create a more user-friendly error
          const errorObj = new Error(message || error);
          (errorObj as any).code = error;
          (errorObj as any).details = errorData.detail;
          throw errorObj;
        }
        
        throw new Error(errorData.message || 'Failed to add part/vehicle');
      }

      const resData = await response.json();
      
      if (resData.status !== 'success') {
        throw new Error(resData.message || 'Invalid response format');
      }

      return resData;
    } catch (error) {
      console.error('Error adding part/vehicle:', error);
      throw error;
    }
  }

  async getInvoicingPartNames(componentIds: number[]): Promise<InvoicingPartName[]> {
    if (!componentIds || componentIds.length === 0) {
      return [];
    }

    try {
      const url = `${API_CONFIG.BASE_URL}/stoc/async/get_invoicing_part_names`;
      const payload = {
        component_ids: componentIds,
      };

      const response = await fetchWithRetry(
        url,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AuthService.getAccessToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          credentials: 'include',
          mode: 'cors',
        },
        3,
        60000
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch invoicing part names');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching invoicing part names:', error);
      throw error;
    }
  }
}

export default SkuService.getInstance(); 
